import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  GraduationCap, Loader2, TrendingUp, TrendingDown, RefreshCw,
  AlertTriangle, Sparkles, MessageCircle,
} from "lucide-react";
import { api } from "@/lib/api";

const CATEGORY_STYLES = {
  positive:   { text: "text-[#10b981]", bg: "bg-[#10b981]/5",  border: "border-[#10b981]/40" },
  structure:  { text: "text-[#3b82f6]", bg: "bg-[#3b82f6]/5",  border: "border-[#3b82f6]/40" },
  discipline: { text: "text-[#f59e0b]", bg: "bg-[#f59e0b]/5",  border: "border-[#f59e0b]/40" },
  risk:       { text: "text-[#ef4444]", bg: "bg-[#ef4444]/5",  border: "border-[#ef4444]/40" },
  general:    { text: "text-[#9ca3af]", bg: "bg-[#1f2937]/40", border: "border-[#1f2937]" },
};

const MISTAKE_LABELS = {
  counter_trend:        "Counter-trend entries",
  news_ignored:         "Traded during news",
  low_confidence:       "Low-confidence setups",
  ati_reduced_ignored:  "Ignored ATI reduce signal",
  trend_aligned:        "Trend-aligned wins",
  win:                  "Wins",
  loss:                 "Generic losses",
  general:              "Other",
};

const LessonBadge = ({ tag, category }) => {
  const style = CATEGORY_STYLES[category] || CATEGORY_STYLES.general;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${style.border} ${style.bg} ${style.text}`}
      data-testid="coach-lesson-badge"
    >
      {MISTAKE_LABELS[tag] || tag}
    </span>
  );
};

const TradeRow = ({ t }) => {
  const isWin = t.state === "closed_win";
  const style = CATEGORY_STYLES[t.category] || CATEGORY_STYLES.general;
  const hasLlm = !!(t.llm_lesson && t.llm_lesson.headline);
  return (
    <div className="border-b border-[#1f2937] py-4" data-testid="coach-trade-row">
      <div className="flex items-start gap-4">
        <div className={`w-1 self-stretch rounded ${isWin ? "bg-[#10b981]" : "bg-[#ef4444]"}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <div className="font-mono font-bold">{t.symbol}</div>
            <div className={`text-xs font-bold ${t.direction === "BUY" ? "text-[#10b981]" : "text-[#ef4444]"}`}>
              {t.direction}
            </div>
            <div className={`font-mono text-sm ${isWin ? "text-[#10b981]" : "text-[#ef4444]"}`}>
              ${(t.pnl || 0).toFixed(2)}
            </div>
            <div className="text-[10px] font-mono text-[#6b7280]">
              {t.closed_at ? new Date(t.closed_at).toLocaleString() : ""}
            </div>
            <LessonBadge tag={t.mistake_tag} category={t.category} />
            {hasLlm && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider border border-[#a855f7]/40 bg-[#a855f7]/10 text-[#a855f7]"
                    data-testid="coach-llm-badge">
                <Sparkles className="w-2.5 h-2.5" /> AI
              </span>
            )}
          </div>
          {hasLlm ? (
            <>
              <div className="text-sm font-bold text-[#a855f7] mb-1">{t.llm_lesson.headline}</div>
              <div className="text-sm text-[#e5e7eb] leading-relaxed">{t.llm_lesson.detail}</div>
            </>
          ) : (
            <div className={`text-sm ${style.text} leading-relaxed`}>{t.lesson}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function Coach() {
  const [loading, setLoading] = useState(true);
  const [latest, setLatest] = useState(null);
  const [recurring, setRecurring] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [trades, setTrades] = useState([]);
  const [refreshingWeekly, setRefreshingWeekly] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, r, w, t] = await Promise.all([
        api.get("/coach/latest"),
        api.get("/coach/recurring?days=14"),
        api.get("/coach/weekly"),
        api.get("/coach/trades?limit=20"),
      ]);
      setLatest(l.data?.lesson);
      setRecurring(r.data);
      setWeekly(w.data);
      setTrades(t.data?.trades || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load coach");
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshWeekly = async () => {
    setRefreshingWeekly(true);
    try {
      const r = await api.post("/coach/weekly/refresh");
      setWeekly(r.data);
      toast.success("Weekly recap refreshed");
    } catch (e) {
      toast.error("Refresh failed");
    } finally {
      setRefreshingWeekly(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 90000);
    return () => clearInterval(iv);
  }, [load]);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]" data-testid="coach-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#3b82f6]" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="coach-page">
      {/* Header */}
      <div>
        <div className="vx-label mb-1 flex items-center gap-2">
          <GraduationCap className="w-3 h-3" /> AI Trading Coach
        </div>
        <h1 className="font-heading text-3xl lg:text-4xl font-black tracking-tighter">
          Learn From Every Trade
        </h1>
        <p className="text-sm text-[#9ca3af] mt-2 max-w-2xl leading-relaxed">
          Your coach reviews every closed trade, spots recurring mistakes across the last two weeks,
          and delivers an honest weekly recap. No trophy talk — just what you did right and what you need to fix.
        </p>
      </div>

      {/* Row 1: Latest lesson + Weekly recap */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Latest lesson */}
        <div className="rounded-lg border border-[#1f2937] bg-[#111827] p-6" data-testid="coach-latest-card">
          <div className="flex items-center gap-2 mb-4">
            <MessageCircle className="w-4 h-4 text-[#3b82f6]" />
            <div className="vx-label">Latest Lesson</div>
          </div>
          {!latest ? (
            <div className="text-sm text-[#9ca3af]">
              No closed trades yet. When you close a trade, your coach lesson appears here.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="font-mono font-bold text-lg">{latest.symbol}</div>
                <div className={`text-sm font-bold ${latest.direction === "BUY" ? "text-[#10b981]" : "text-[#ef4444]"}`}>
                  {latest.direction}
                </div>
                {latest.state === "closed_win" ? (
                  <TrendingUp className="w-4 h-4 text-[#10b981]" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-[#ef4444]" />
                )}
                <div className={`font-mono ${latest.state === "closed_win" ? "text-[#10b981]" : "text-[#ef4444]"}`}>
                  ${(latest.pnl || 0).toFixed(2)}
                </div>
                <LessonBadge tag={latest.mistake_tag} category={latest.category} />
                {latest.llm_lesson?.headline && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider border border-[#a855f7]/40 bg-[#a855f7]/10 text-[#a855f7]">
                    <Sparkles className="w-2.5 h-2.5" /> AI COACH
                  </span>
                )}
              </div>
              {latest.llm_lesson?.headline ? (
                <>
                  <div className="text-base font-bold text-[#a855f7]" data-testid="coach-latest-headline">
                    {latest.llm_lesson.headline}
                  </div>
                  <div className="text-sm leading-relaxed text-[#e5e7eb]">{latest.llm_lesson.detail}</div>
                  <div className="text-[10px] text-[#6b7280] italic pt-1 border-t border-[#1f2937]">
                    Rule-based note: {latest.lesson}
                  </div>
                </>
              ) : (
                <div className="text-sm leading-relaxed text-[#e5e7eb]">{latest.lesson}</div>
              )}
              {latest.closed_at && (
                <div className="text-[10px] font-mono text-[#6b7280]">
                  Closed {new Date(latest.closed_at).toLocaleString()}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Weekly recap */}
        <div className="rounded-lg border border-[#1f2937] bg-[#111827] p-6" data-testid="coach-weekly-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#a855f7]" />
              <div className="vx-label">Weekly Recap</div>
            </div>
            <button
              onClick={refreshWeekly}
              disabled={refreshingWeekly}
              className="text-[10px] font-mono uppercase tracking-widest text-[#9ca3af] hover:text-[#3b82f6] transition-colors disabled:opacity-40 flex items-center gap-1"
              data-testid="coach-refresh-weekly-btn"
            >
              <RefreshCw className={`w-3 h-3 ${refreshingWeekly ? "animate-spin" : ""}`} />
              {refreshingWeekly ? "Refreshing…" : "Refresh"}
            </button>
          </div>
          {!weekly ? (
            <div className="text-sm text-[#9ca3af]">No recap yet.</div>
          ) : (
            <div className="space-y-4">
              {weekly.trades_analyzed > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center">
                    <div className="text-[9px] font-mono uppercase tracking-widest text-[#6b7280]">Trades</div>
                    <div className="font-heading text-2xl font-black">{weekly.trades_analyzed}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[9px] font-mono uppercase tracking-widest text-[#6b7280]">Win Rate</div>
                    <div className="font-heading text-2xl font-black text-[#3b82f6]">{weekly.win_rate_pct}%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[9px] font-mono uppercase tracking-widest text-[#6b7280]">Net P&L</div>
                    <div className={`font-heading text-2xl font-black ${(weekly.net_pnl_usd || 0) >= 0 ? "text-[#10b981]" : "text-[#ef4444]"}`}>
                      ${(weekly.net_pnl_usd || 0).toFixed(0)}
                    </div>
                  </div>
                </div>
              )}
              <div className="text-sm leading-relaxed text-[#e5e7eb] border-l-2 border-[#a855f7]/40 pl-3 italic">
                {weekly.recap}
              </div>
              {weekly.lessons && weekly.lessons.length > 0 && (
                <div className="space-y-2 pt-2 border-t border-[#1f2937]">
                  {weekly.lessons.map((L, i) => (
                    <div key={i} data-testid="coach-lesson-item">
                      <div className="text-xs font-bold text-[#a855f7]">{L.headline}</div>
                      <div className="text-xs text-[#9ca3af] leading-relaxed mt-0.5">{L.detail}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Recurring mistakes */}
      {recurring && recurring.top_mistakes && recurring.top_mistakes.length > 0 && (
        <div className="rounded-lg border border-[#1f2937] bg-[#111827] p-6" data-testid="coach-recurring-card">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-[#f59e0b]" />
            <div className="vx-label">Recurring Mistakes — Last {recurring.days} Days</div>
            <div className="ml-auto text-[10px] font-mono text-[#6b7280]">
              {recurring.losses_analyzed} losses analysed · ${recurring.total_loss_usd.toFixed(2)} damage
            </div>
          </div>
          <div className="space-y-3">
            {recurring.top_mistakes.map((m) => (
              <div
                key={m.mistake_tag}
                className="grid grid-cols-12 gap-3 items-start p-3 rounded border border-[#1f2937] hover:border-[#3b82f6]/30"
                data-testid={`coach-mistake-${m.mistake_tag}`}
              >
                <div className="col-span-3">
                  <div className="text-sm font-bold text-[#f59e0b]">
                    {MISTAKE_LABELS[m.mistake_tag] || m.mistake_tag}
                  </div>
                  <div className="text-[10px] font-mono text-[#6b7280] mt-0.5">
                    {m.occurrences}× ({m.share_pct}% of losses)
                  </div>
                  {m.sample_symbol && (
                    <div className="text-[10px] font-mono text-[#6b7280]">e.g. {m.sample_symbol}</div>
                  )}
                </div>
                <div className="col-span-9 text-sm text-[#e5e7eb] leading-relaxed">
                  {m.example_lesson}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-trade lessons */}
      <div className="rounded-lg border border-[#1f2937] bg-[#111827]" data-testid="coach-trades-list">
        <div className="px-6 py-4 border-b border-[#1f2937] flex items-center justify-between">
          <div className="vx-label flex items-center gap-2">
            <MessageCircle className="w-3 h-3" /> Per-Trade Lessons
          </div>
          <div className="text-[10px] font-mono text-[#6b7280]">Last {trades.length} closed</div>
        </div>
        {trades.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-[#9ca3af]">
            No closed trades yet.
          </div>
        ) : (
          <div className="px-6">
            {trades.map(t => <TradeRow key={t.id} t={t} />)}
          </div>
        )}
      </div>
    </div>
  );
}
